/**
 * ContainerPermissionConfig — Permission levels and per-container-type defaults
 *
 * Defines the 5-level permission system from issue #333. Every inventory-bearing
 * object (chests, workstations, characters, blueprints, item piles) supports
 * the same vocabulary of permission levels:
 *
 *   Browse   — see container contents and prices
 *   Buy      — purchase items at listed prices
 *   Deposit  — add items to the container
 *   Withdraw — remove items from the container
 *   Manage   — change permissions, prices, and settings
 *
 * Levels are independent grants — having Withdraw does not imply Browse, and
 * vice versa. The single exception is `Manage`, which implicitly grants every
 * other level (a manager can always do everything an owner can).
 *
 * Per-container-type defaults define what permissions exist for the
 * "everyone else" pseudo-player when no explicit grant is recorded. Owners
 * always have all 5 permissions on their own containers regardless of defaults.
 *
 * @module shared/ContainerPermissionConfig
 * @see docs/Architecture_Economy.md § Container Permissions
 * @see shared/ContainerPermissionService.js
 */

// =============================================================================
// Permission level enum
// =============================================================================

/**
 * The 5 permission levels.
 * @readonly
 * @enum {string}
 */
export const ContainerPermission = Object.freeze({
	BROWSE: 'browse',
	BUY: 'buy',
	DEPOSIT: 'deposit',
	WITHDRAW: 'withdraw',
	MANAGE: 'manage',
});

/**
 * All valid permission levels as an array.
 * @type {string[]}
 */
export const CONTAINER_PERMISSIONS = Object.freeze(Object.values(ContainerPermission));

/**
 * Validate a permission level string.
 * @param {string} level
 * @returns {boolean}
 */
export function isValidContainerPermission(level) {
	return CONTAINER_PERMISSIONS.includes(level);
}

// =============================================================================
// Container types
// =============================================================================

/**
 * Logical container categories used to look up default permissions.
 * Maps from "what kind of inventory-bearing thing is this?" to a default profile.
 *
 * @readonly
 * @enum {string}
 */
export const ContainerKind = Object.freeze({
	/** Player character on-person inventory */
	CHARACTER: 'character',
	/** Storage chest, barrel, etc. */
	STORAGE: 'storage',
	/** Workstation inventory (forge, crafting table, etc.) */
	WORKSTATION: 'workstation',
	/** In-progress construction site */
	BLUEPRINT: 'blueprint',
	/** Dropped items on the ground */
	ITEM_PILE: 'item_pile',
});

/**
 * Default permission profile per container kind.
 *
 * Each entry lists the permissions implicitly granted to "everyone else"
 * (any player who is neither the owner nor has an explicit grant). Permissions
 * not listed default to deny.
 *
 * Design rationale (Architecture_Economy.md § Container Permissions):
 * - Character inventories are private by default — owner grants Browse/Buy
 *   to specific players (e.g. allies, retainer NPCs).
 * - Storage / workstation containers default to Browse + Buy public so the
 *   passive marketplace can list their contents; deposit/withdraw require an
 *   explicit grant.
 * - Blueprints default to Browse + Deposit public so anyone can contribute
 *   construction materials; withdraw/manage require an explicit grant.
 * - Item piles default to fully public for the dropper-ownership window —
 *   ownership timing is enforced separately by the pile's `ownerId` and
 *   ownership-expiry timestamp, not by the permission table.
 *
 * @type {Object.<string, string[]>}
 */
export const DefaultContainerPermissions = Object.freeze({
	[ContainerKind.CHARACTER]: Object.freeze([]),
	[ContainerKind.STORAGE]: Object.freeze([
		ContainerPermission.BROWSE,
		ContainerPermission.BUY,
	]),
	[ContainerKind.WORKSTATION]: Object.freeze([
		ContainerPermission.BROWSE,
		ContainerPermission.BUY,
	]),
	[ContainerKind.BLUEPRINT]: Object.freeze([
		ContainerPermission.BROWSE,
		ContainerPermission.DEPOSIT,
	]),
	[ContainerKind.ITEM_PILE]: Object.freeze([
		ContainerPermission.BROWSE,
		ContainerPermission.WITHDRAW,
	]),
});

/**
 * Get the default permission set for a container kind. Returns an empty
 * (frozen) array for unknown kinds — unknown containers are private by
 * default (deny-by-default policy).
 *
 * @param {string} kind  One of ContainerKind values
 * @returns {string[]}   Array of ContainerPermission values
 */
export function getDefaultPermissionsForKind(kind) {
	return DefaultContainerPermissions[kind] ?? Object.freeze([]);
}

// =============================================================================
// Public-default override sentinel (#421)
// =============================================================================

/**
 * Reserved `characterId` value meaning "everyone" — used by
 * {@link ContainerPermissionGrant} rows that override a container's
 * per-kind public default (from {@link DefaultContainerPermissions}) instead
 * of granting a real character extra access.
 *
 * Real character ids are always `> 0` (per the `objects` AUTO_INCREMENT PK),
 * so `0` is safe to reserve. The strict FK from `container_permissions` to
 * `characters` is dropped to allow this sentinel row to be inserted; an
 * app-level `characterId >= 0` check replaces it.
 *
 * Resolution order (see {@link canPerform}):
 *   1. Owner / explicit per-character grants (as before).
 *   2. If *any* sentinel grant rows exist for the container, they are
 *      authoritative for "everyone else" — the per-kind config default is
 *      ignored entirely (even if the sentinel rows grant fewer permissions).
 *   3. Otherwise, fall back to {@link DefaultContainerPermissions}[kind].
 *
 * @type {number}
 */
export const PUBLIC_TARGET_CHARACTER_ID = 0;

/**
 * Whether a grant row targets the "everyone" sentinel.
 * @param {{characterId?: number}} grant
 * @returns {boolean}
 */
export function isPublicTargetGrant(grant) {
	return !!grant && grant.characterId === PUBLIC_TARGET_CHARACTER_ID;
}

/**
 * Resolve the effective "everyone else" permission set for a container kind,
 * honoring sentinel public-default override grants when present.
 *
 * @param {string} kind                    ContainerKind for default-permission lookup
 * @param {Array<{characterId?: number, level?: string}>} [grants=[]]
 *   Explicit permission grants for this container (raw or ContainerPermissionGrant).
 * @returns {string[]}  Array of ContainerPermission values
 */
export function getEffectivePublicPermissionsForKind(kind, grants = []) {
	const sentinelLevels = Array.isArray(grants)
		? grants.filter(isPublicTargetGrant).map(g => g.level)
		: [];
	if (sentinelLevels.length > 0) {
		return sentinelLevels.filter(isValidContainerPermission);
	}
	return getDefaultPermissionsForKind(kind);
}

// =============================================================================
// Item pile ownership
// =============================================================================

/**
 * Default duration (in seconds) that the dropper retains exclusive ownership
 * of an item pile before it becomes free-for-all.
 *
 * Open question (Architecture_Economy.md § Item Pile Ownership Consolidation):
 * the exact value is not finalized; 5 minutes is a reasonable starting point
 * matching the trade-ui mock's "4 min remaining" example.
 *
 * Tile claims extend ownership indefinitely — see ContainerPermissionService.
 *
 * @type {number}
 */
export const ITEM_PILE_OWNERSHIP_DURATION_SECONDS = 5 * 60;
